import { el } from "../lib/dom.js";
import { icon, type IconId } from "../lib/icon.js";
import { aimMark, type AimDeps, type AimHandlers } from "./aim.js";

/**
 * The tab bar (UX spec v3 §10.2, §C.3): Home · Activity · the assistant's mark ·
 * Invest · Me, one component on Home (01), Activity (11), Invest and Me (12). The
 * current tab is `aria-current="page"`; tapping it again does nothing. The mark is the
 * way into the assistant from any tab: tap = the chat, text first; hold or slide up =
 * the chat, listening (round C replaced the tab-bar mic and the Ask tile with it).
 */
export type Tab = "home" | "activity" | "invest" | "me";

export interface TabHandlers extends AimHandlers {
  onTab(tab: Tab): void;
}

export function tabBar(
  current: Tab,
  handlers: TabHandlers,
  opts: {
    /** On a screen pushed under a tab (judge mode's Proof under Me), tapping that tab goes back to it. */
    readonly pushed?: boolean;
    /** The mark's clock (tests); the browser's by default. */
    readonly aim?: AimDeps;
  } = {},
): { dock: HTMLElement; mark: HTMLButtonElement } {
  const mark = aimMark(handlers, opts.aim);
  const tab = (id: IconId, label: string, target: Tab | undefined): HTMLButtonElement => {
    const on = target === current;
    const b = el("button", { class: on ? "raw tab on" : "raw tab", type: "button" }, [icon(id), label]);
    if (on) b.setAttribute("aria-current", "page");
    if (target === undefined) b.setAttribute("aria-disabled", "true");
    b.addEventListener("click", () => {
      if (target !== undefined && (!on || opts.pushed === true)) handlers.onTab(target);
    });
    return b;
  };
  const dock = el("div", { class: "dock" }, [
    el("nav", { class: "tabs", aria: { label: "Main" } }, [
      tab("i-home", "Home", "home"),
      tab("i-list", "Activity", "activity"),
      mark.button,
      tab("i-pie", "Invest", "invest"),
      tab("i-user", "Me", "me"),
      ...mark.extras,
    ]),
  ]);
  return { dock, mark: mark.button };
}
